import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

import { captureEvent, tierLabel } from "../analytics";
import { type ChatTarget, streamChat, streamGreeting } from "../api/client";
import { useAuth } from "../auth/useAuth";
import type { ChatTurn } from "../types/api";

/**
 * ChatPanel — Task 9.0/9.1.
 *
 * Two variants:
 *  - "compact" (paid score view): a follow-up chat below the score.
 *  - "forward" (free tier): the PRIMARY surface — full chat with suggested
 *    first-question chips, a turn-remaining badge, and an inline upgrade
 *    CTA when the free turn limit is hit.
 *
 * Target is either a scoreId (paid, discussing a verified Tilt Map) or a
 * placeId (free, discussing a property with no score).
 */

interface Props {
  scoreId?: string;
  placeId?: string;
  initialHistory?: ChatTurn[];
  variant?: "compact" | "forward";
  /** Free-tier per-session turn limit (default 10, Req 9.6). */
  turnLimit?: number;
  /** Invoked when a free user hits the limit and clicks the upgrade CTA. */
  onUpgradeClick?: () => void;
  /** Clickable starter questions for the empty state (defaults to the free set). */
  suggestions?: string[];
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  streaming?: boolean;
  toolStatus?: string;
  note?: string;
  error?: string;
}

const MAX_CONTENT = 2000;

const SUGGESTED_QUESTIONS = [
  "What's the negotiation room here?",
  "Any red flags I should know about?",
  "What should I offer?",
  "What's the flood risk?",
  "How are the school zones?",
];

// Rotating "working" status shown while an answer is in flight. The chat is
// buffered (no token streaming), so this replaces a dead blinking cursor with
// a sense of progress — people wait happily if they know the AI is working.
// Once it reaches THINKING_TAIL it loops among the tail messages so it keeps
// moving instead of parking on one line for the rest of the wait.
const THINKING_STEPS = [
  "Searching public records & listings…",
  "Checking flood, hazard & tax data…",
  "Pulling comparable sales…",
  "Cross-referencing county records…",
  "Weighing the negotiation signals…",
  "Writing your answer…",
  "Pulling it together…",
  "Almost there…",
];
const THINKING_TAIL = 5; // loop "Writing… / Pulling together… / Almost there…"

export function ChatPanel({
  scoreId,
  placeId,
  initialHistory = [],
  variant = "compact",
  turnLimit = 10,
  onUpgradeClick,
  suggestions = SUGGESTED_QUESTIONS,
}: Props) {
  const auth = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>(
    initialHistory.map((t) => ({ role: t.role, content: t.content })),
  );
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [limitHit, setLimitHit] = useState(false);
  const [thinkIdx, setThinkIdx] = useState(0);
  const [showPrefsNudge, setShowPrefsNudge] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const greetingFiredRef = useRef(false);

  const isForward = variant === "forward";
  const userTurns = messages.filter((m) => m.role === "user").length;
  const turnsRemaining = Math.max(0, turnLimit - userTurns);

  // Auto-greeting for Pro users (chat-personalization Task 11.1, Req 3.1/3.7/3.8/3.9/5.2).
  // Fire once when:
  //   - scoreId is present (scored property)
  //   - auth.tier is "paid" (Pro user)
  //   - no initial history (don't re-greet on subsequent opens)
  //   - greeting hasn't already been fired this mount
  useEffect(() => {
    if (greetingFiredRef.current) return;
    if (!scoreId) return;
    if (auth.tier !== "paid") return;
    if (initialHistory.length > 0) return;

    greetingFiredRef.current = true;

    const controller = new AbortController();
    let receivedTokens = false;

    // Helper: show the nudge when greeting falls back (profile_context was null
    // because preferences aren't set) — but only if the user hasn't dismissed it.
    const maybeShowNudge = () => {
      if (!localStorage.getItem("pt.prefsNudgeDismissed")) {
        setShowPrefsNudge(true);
      }
    };

    (async () => {
      try {
        // Insert a streaming assistant placeholder immediately so the panel
        // shows the thinking state while the greeting loads.
        const placeholder: ChatMessage = { role: "assistant", content: "", streaming: true };
        setMessages([placeholder]);

        for await (const ev of streamGreeting(scoreId, controller.signal)) {
          if (ev.event === "token") {
            receivedTokens = true;
            setMessages((prev) => {
              if (prev.length === 0) return prev;
              const last = prev[prev.length - 1];
              if (last.role !== "assistant") return prev;
              return [
                ...prev.slice(0, -1),
                { ...last, content: last.content + ev.data },
              ];
            });
          } else if (ev.event === "done") {
            // If no tokens were received, fall back to empty chat (no greeting).
            if (!receivedTokens) {
              setMessages([]);
              maybeShowNudge();
            } else {
              setMessages((prev) => {
                if (prev.length === 0) return prev;
                const last = prev[prev.length - 1];
                if (last.role !== "assistant") return prev;
                return [
                  ...prev.slice(0, -1),
                  { ...last, streaming: false },
                ];
              });
            }
          } else if (ev.event === "error") {
            // Silently fall back — no error shown (Req 3.9).
            setMessages([]);
            maybeShowNudge();
          }
        }

        // Stream ended without a done event and no tokens — clear placeholder.
        if (!receivedTokens) {
          setMessages((prev) => {
            if (prev.length === 1 && prev[0].role === "assistant" && !prev[0].content) {
              return [];
            }
            return prev;
          });
          maybeShowNudge();
        }
      } catch {
        // Network error / timeout / abort — fall back silently to empty chat.
        if (!controller.signal.aborted) {
          setMessages([]);
          maybeShowNudge();
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [scoreId, auth.tier, initialHistory.length]);

  // When a new turn starts, scroll that question to the TOP of the chat box
  // so the (often long) answer reads top-to-bottom. The old behavior pinned to
  // the bottom on every streamed token, leaving you parked at the END of the
  // answer and forcing a scroll-up to start reading.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const userMsgs = list.querySelectorAll<HTMLElement>(".chat-message.chat-user");
    const last = userMsgs[userMsgs.length - 1];
    if (!last) return;
    const top =
      last.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
    list.scrollTop = Math.max(0, top - 8);
  }, [userTurns]);

  // Advance the "working" status every ~2.8s while a request is in flight.
  useEffect(() => {
    if (!sending) return;
    setThinkIdx(0);
    const id = window.setInterval(() => {
      // Advance, then loop within the tail so it never parks on one line.
      setThinkIdx((i) => (i + 1 >= THINKING_STEPS.length ? THINKING_TAIL : i + 1));
    }, 2400);
    return () => window.clearInterval(id);
  }, [sending]);

  function targetFor(): ChatTarget | null {
    if (scoreId) return { scoreId };
    if (placeId) return { placeId };
    return null;
  }

  function dismissPrefsNudge() {
    localStorage.setItem("pt.prefsNudgeDismissed", "1");
    setShowPrefsNudge(false);
  }

  async function send(content: string) {
    const trimmed = content.trim();
    if (!trimmed || sending || limitHit) return;
    if (trimmed.length > MAX_CONTENT) return;
    const target = targetFor();
    if (!target) return;

    setDraft("");
    setSending(true);
    captureEvent("chat_message_sent", {
      tier: tierLabel(auth.tier),
      context: scoreId ? "verified_report" : "property",
    });
    const userMsg: ChatMessage = { role: "user", content: trimmed };
    const placeholder: ChatMessage = { role: "assistant", content: "", streaming: true };
    setMessages((prev) => [...prev, userMsg, placeholder]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      for await (const ev of streamChat(target, trimmed, controller.signal)) {
        // Capture outside the state updater (updaters may run twice under
        // StrictMode). Mirrors the turn-limit check inside it.
        if (
          ev.event === "error" &&
          (ev.data.code === "turn_limit_exceeded" || /limit/i.test(ev.data.message))
        ) {
          captureEvent("paywall_hit", { reason: "chat_turn_limit" });
        }
        setMessages((prev) => {
          if (prev.length === 0) return prev;
          const lastIdx = prev.length - 1;
          const last = prev[lastIdx];
          if (last.role !== "assistant") return prev;
          const patched: ChatMessage = { ...last };
          if (ev.event === "token") {
            patched.content = last.content + ev.data;
          } else if (ev.event === "tool_call") {
            patched.toolStatus = `Checking ${ev.data.tool}…`;
          } else if (ev.event === "system_note") {
            patched.note = ev.data.message;
          } else if (ev.event === "done") {
            patched.streaming = false;
            patched.toolStatus = undefined;
          } else if (ev.event === "error") {
            patched.error = ev.data.message;
            patched.streaming = false;
            // Free-tier turn-limit signal from the backend quota gate.
            if (ev.data.code === "turn_limit_exceeded" || /limit/i.test(ev.data.message)) {
              setLimitHit(true);
            }
          }
          return [...prev.slice(0, lastIdx), patched];
        });
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === "assistant") {
          last.error = (e as Error).message;
          last.streaming = false;
        }
        return next;
      });
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  }

  function handleSend(e: React.FormEvent) {
    e.preventDefault();
    void send(draft);
  }

  function handleStop() {
    abortRef.current?.abort();
  }

  return (
    <section
      className={`chat-panel ${isForward ? "chat-panel-forward" : "chat-panel-compact"}`}
      aria-label={isForward ? "Property chat" : "Follow-up chat"}
    >
      {isForward ? (
        <header className="chat-forward-header">
          <h2>Ask anything about this property</h2>
          <p className="chat-disclaimer">
            Grounded in public records + web data. A research aid, not advice.
          </p>
          {!limitHit && (
            <span className="chat-turns-badge" aria-live="polite">
              {turnsRemaining} {turnsRemaining === 1 ? "question" : "questions"} left
            </span>
          )}
        </header>
      ) : (
        <>
          <h2>Ask a follow-up question</h2>
          <p className="chat-disclaimer">Chat is a research aid, not advice.</p>
        </>
      )}

      <div className="chat-messages" ref={listRef}>
        {messages.length === 0 && (
          <div className="chat-suggestions">
            {showPrefsNudge && (
              <div className="chat-prefs-nudge">
                <span className="chat-prefs-nudge-text">
                  ✨ Personalize your chat — tell the AI what you&rsquo;re looking for and it&rsquo;ll tailor its analysis.
                </span>
                <a href="/account" className="chat-prefs-nudge-link">Set preferences →</a>
                <button
                  type="button"
                  className="chat-prefs-nudge-dismiss"
                  onClick={dismissPrefsNudge}
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            )}
            <p className="chat-empty">Try one of these to start:</p>
            <div className="chat-chips">
              {suggestions.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="chat-chip"
                  disabled={sending}
                  onClick={() => void send(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => {
          // Show the rotating "working" status on the in-flight assistant
          // placeholder (streaming, no content yet) instead of a dead cursor.
          const thinkingLabel =
            m.role === "assistant" && m.streaming && !m.content
              ? THINKING_STEPS[thinkIdx]
              : null;
          return <ChatMessageView key={i} message={m} thinkingLabel={thinkingLabel} />;
        })}
      </div>

      {limitHit ? (
        <div className="chat-limit-cta" role="alert">
          <p>You&rsquo;ve reached the free chat limit for now.</p>
          {onUpgradeClick && (
            <button type="button" className="upgrade-cta" onClick={onUpgradeClick}>
              Unlock unlimited chat + verified Tilt Map
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={handleSend} className="chat-input-row">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={isForward ? "Ask about price, risks, schools, flood…" : "Type a follow-up question…"}
            maxLength={MAX_CONTENT}
            aria-label="Chat input"
            disabled={sending}
          />
          {sending ? (
            <button type="button" onClick={handleStop}>
              Stop
            </button>
          ) : (
            <button type="submit" disabled={!draft.trim()}>
              Send
            </button>
          )}
        </form>
      )}

      {isForward && onUpgradeClick && !limitHit && (
        <p className="chat-upgrade-foot">
          Answers draw on the public web. For the owner&rsquo;s equity profile,
          licensed data, and your verified Tilt Map,{" "}
          <button type="button" className="link-button" onClick={onUpgradeClick}>
            Upgrade to PriceTilt Pro
          </button>
          .
        </p>
      )}
    </section>
  );
}

function ChatMessageView({
  message,
  thinkingLabel,
}: {
  message: ChatMessage;
  thinkingLabel?: string | null;
}) {
  // Assistant turns render markdown (bold, bullets, links for citations —
  // Task 9.1). User turns stay plain text. Links open in a new tab.
  return (
    <div className={`chat-message chat-${message.role}`}>
      <div className="chat-role">{message.role === "user" ? "You" : "PriceTilt"}</div>
      <div className="chat-content">
        {message.role === "assistant" ? (
          thinkingLabel ? (
            <div className="chat-thinking" aria-live="polite">
              <span className="chat-thinking-spinner" aria-hidden />
              {thinkingLabel}
            </div>
          ) : (
            <div className="chat-markdown">
              <ReactMarkdown
                components={{
                  a: (props) => (
                    <a {...props} target="_blank" rel="noreferrer noopener" />
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )
        ) : (
          message.content
        )}
        {message.streaming && message.content && <span className="cursor">▍</span>}
      </div>
      {message.toolStatus && <div className="tool-status">{message.toolStatus}</div>}
      {message.note && <div className="chat-note">{message.note}</div>}
      {message.error && <div className="chat-error">{message.error}</div>}
    </div>
  );
}
